import { ASSETS, CONTACT } from "@/lib/constants";
import LeadForm from "./LeadForm";
import { Phone, Mail, BadgeCheck } from "lucide-react";
import { useApplyIntent } from "@/components/ApplyIntent";
import { useRateCheckRouter } from "@/components/RateCheckRouter";
import { useMLO } from "@/lib/mloContext";
import { resolveApplyUrl } from "@/lib/applyTarget";

export default function MeetJason() {
  const { openIntent } = useApplyIntent();
  const { openRouter } = useRateCheckRouter();
  const mlo = useMLO();
  const intentOpts = { applyUrl: resolveApplyUrl(mlo), mloSlug: mlo?.slug, mloName: mlo?.name, mloPhone: mlo?.phone };
  const name = mlo?.name || "Jason Gray";
  const first = name.split(" ")[0];
  const nmls = mlo?.nmls || CONTACT.nmls;
  const years = mlo?.years_experience;
  // Photo source (priority: secondary → primary → Jason bundled for /jason only → logomark fallback)
  const hasPortrait = !!(mlo?.photoSecondaryUrl || mlo?.photoUrl) || mlo?.slug === "jason" || !mlo;
  const photo = mlo?.photoSecondaryUrl
    || mlo?.photoUrl
    || (mlo?.slug === "jason" || !mlo ? ASSETS.headshot : null);
  const phone = mlo?.phone || CONTACT.phoneDisplay;
  const phoneHref = mlo?.phone ? `tel:${mlo.phone.replace(/[^+0-9]/g, "")}` : CONTACT.phoneHref;
  const email = mlo ? (mlo.email || "") : CONTACT.email;
  const emailHref = mlo ? (mlo.email ? `mailto:${mlo.email}` : "") : CONTACT.emailHref;

  // Jason has custom stats; other MLOs show the Years pill only (if set).
  const isJason = mlo?.slug === "jason" || !mlo;
  return (
    <section id="jason" data-testid="jason-section" className="section-y bg-linen relative">
      <div className="container-grc">
        <div className="max-w-3xl">
          <p className="text-xs font-bold tracking-[0.22em] uppercase text-teal">
            Meet your loan officer
          </p>
          <h2 className="mt-4 font-heading font-medium text-4xl sm:text-5xl text-balance">
            {mlo ? (
              <>Meet {name}.</>
            ) : (
              <>30 years of mortgages, one straight-shooting advocate.</>
            )}
          </h2>
          {mlo?.tagline && (
            <p className="mt-3 text-lg text-ink-600 max-w-2xl leading-relaxed">{mlo.tagline}</p>
          )}
        </div>

        <div className="mt-14 grid grid-cols-12 gap-8 lg:gap-12 items-start">
          {/* Headshot + credentials */}
          <div className="col-span-12 lg:col-span-5">
            <div className="relative rounded-3xl overflow-hidden shadow-lift bg-white border border-linen-border">
              {hasPortrait && photo ? (
                <img
                  src={photo}
                  alt={`${name}, mortgage loan officer at NEXA Lending`}
                  className="w-full aspect-[4/5] object-cover"
                  loading="lazy"
                />
              ) : (
                <div className="w-full aspect-[4/5] flex items-center justify-center bg-gradient-to-br from-navy-deep via-[#0F2A47] to-[#17385e] p-16">
                  <img
                    src={ASSETS.logoMark}
                    alt="Great Rate Check"
                    className="w-full h-full object-contain opacity-90"
                    loading="lazy"
                  />
                </div>
              )}
              <div className="absolute left-4 bottom-4 bg-white/95 backdrop-blur rounded-2xl px-4 py-3 shadow-soft">
                <p className="font-heading font-medium text-lg leading-tight">{name}</p>
                <p className="text-xs text-ink-500">Mortgage Loan Officer · NMLS #{nmls}</p>
              </div>
            </div>

            <div className="mt-6 grid grid-cols-1 gap-3">
              <ContactRow icon={<Phone size={16} />} label={phone} href={phoneHref} testid="jason-phone" />
              {email && <ContactRow icon={<Mail size={16} />} label={email} href={emailHref} testid="jason-email" />}
              <ContactRow icon={<BadgeCheck size={16} />} label={`NEXA Lending · NMLS #${CONTACT.companyNmls}`} testid="jason-company" />
            </div>
          </div>

          {/* Bio + Form */}
          <div className="col-span-12 lg:col-span-7 space-y-8">
            <LeadForm />

            <div className="rounded-3xl bg-white border border-linen-border p-8 sm:p-10 shadow-soft">
              {mlo && mlo.bio ? (
                <p className="text-lg leading-relaxed text-ink-700 whitespace-pre-line">{mlo.bio}</p>
              ) : isJason ? (
                <>
                  <p className="text-lg leading-relaxed text-ink-700">
                    {name} has spent more than <strong className="text-navy">three decades</strong>{" "}
                    helping families navigate one of the largest financial decisions of
                    their lives — the home loan. Serving clients nationwide as a Loan
                    Officer with{" "}
                    <strong className="text-navy">NEXA Lending</strong>, {first} blends
                    deep industry expertise with a genuine, plain-English approach.
                  </p>
                  <p className="mt-4 text-lg leading-relaxed text-ink-700">
                    Whether you're buying your first home, refinancing to save, or
                    investing in property, {first} will help you understand your
                    options, shop competitive rates across NEXA's lender network, and
                    close with confidence — no pressure, no jargon.
                  </p>
                </>
              ) : (
                <p className="text-lg leading-relaxed text-ink-700">
                  {first} is a licensed mortgage loan officer with{" "}
                  <strong className="text-navy">NEXA Lending</strong> — direct guidance,
                  plain-English answers, and a one-officer, start-to-close experience.
                  No call center, no rotating handoffs. Ask about purchase, refinance,
                  cash-out, or investment scenarios.
                </p>
              )}

              {isJason ? (
                <div className="mt-8 grid grid-cols-3 gap-4">
                  <Stat n="30+" label="Years originating" />
                  <Stat n="$1B+" label="Funded lifetime" />
                  <Stat n="5★" label="Client experience" />
                </div>
              ) : years > 0 ? (
                <div className="mt-8 grid grid-cols-1 sm:max-w-xs gap-4">
                  <Stat n={`${years}+`} label="Years originating" />
                </div>
              ) : null}

              <div className="mt-8 flex flex-wrap gap-3">
                <button
                  type="button"
                  data-testid="jason-check-rate-cta"
                  onClick={() => mlo ? openIntent("jason-check-rate-cta", intentOpts) : openRouter("jason-check-rate-cta")}
                  className="inline-flex items-center gap-2 bg-lime hover:bg-lime-hover text-navy-deep font-semibold px-6 py-3 rounded-full shadow-cta hover:shadow-cta-hover transition-all hover:-translate-y-0.5"
                >
                  Check My Rate →
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ContactRow({ icon, label, href, testid }) {
  const Cmp = href ? "a" : "div";
  return (
    <Cmp
      data-testid={testid}
      href={href}
      className="flex items-center gap-3 rounded-xl bg-white border border-linen-border px-4 py-3 text-sm text-navy hover:border-teal hover:shadow-soft transition-all"
    >
      <span className="text-teal">{icon}</span>
      <span className="font-medium">{label}</span>
    </Cmp>
  );
}

function Stat({ n, label }) {
  return (
    <div className="rounded-2xl bg-linen p-4 border border-linen-border">
      <p className="font-heading font-medium text-3xl text-navy">{n}</p>
      <p className="text-xs text-ink-500 mt-1">{label}</p>
    </div>
  );
}
